/**
 * @file     regionData.ts
 * @readme   ./CLAUDE.md
 * @purpose  Static data for Greater Vancouver municipalities displayed in the interactive map detail panel.
 */

export type RegionInfo = {
  /** i18n key slug for this region (e.g. 'vancouver' -> pages.neighborhoodGuides.regions.vancouver.*) */
  i18nKey: string;
  name: string;
  description: string;
  highlights: string[];
};

export const REGION_DATA: Record<string, RegionInfo> = {
  'Vancouver': {
    i18nKey: 'vancouver',
    name: 'Vancouver',
    description: 'The heart of Metro Vancouver, with diverse neighbourhoods from Downtown condos to Westside family homes. Strong demand across all segments.',
    highlights: [
      'Downtown, Kitsilano, Mount Pleasant, Kerrisdale, Dunbar',
      'Transit-rich with SkyTrain, bus rapid transit',
      'Median detached home: $2.1M+',
      'Top school districts and universities',
    ],
  },
  'West Van': {
    i18nKey: 'westVan',
    name: 'West Vancouver',
    description: 'Prestigious waterfront community on the North Shore. Known for luxury estates, ocean views, and proximity to mountain recreation.',
    highlights: [
      'British Properties, Ambleside, Dundarave',
      'Highest median home prices in Metro Vancouver',
      'Mountain and ocean recreation at your doorstep',
      'Excellent public and private schools',
    ],
  },
  'North Van District': {
    i18nKey: 'northVanDistrict',
    name: 'North Vancouver District',
    description: 'Mountain-adjacent living with trails, ski hills, and growing urban centres. A blend of established neighbourhoods and new developments.',
    highlights: [
      'Lynn Valley, Deep Cove, Edgemont Village',
      'Gateway to Grouse Mountain and local trails',
      'Growing SkyTrain extension planned',
      'Family-friendly suburban character',
    ],
  },
  'North Van City': {
    i18nKey: 'northVanCity',
    name: 'City of North Vancouver',
    description: 'Compact, transit-connected urban centre on the North Shore. Walking distance to Lonsdale Quay and SeaBus.',
    highlights: [
      'Lonsdale corridor, Lower Lonsdale waterfront',
      'SeaBus connection to downtown in 12 minutes',
      'Rapidly developing condo and rental market',
      'Vibrant restaurant and brewery scene',
    ],
  },
  'Burnaby': {
    i18nKey: 'burnaby',
    name: 'Burnaby',
    description: 'Metro Vancouver\'s geographic centre with excellent SkyTrain access on two lines, major town centres, and diverse housing stock.',
    highlights: [
      'Metrotown, Brentwood, Lougheed, all SkyTrain-connected',
      'BCIT and SFU university presence',
      'Strong rental market and new developments',
      'Central Park, Burnaby Lake recreation',
    ],
  },
  'Richmond': {
    i18nKey: 'richmond',
    name: 'Richmond',
    description: 'Gateway city with YVR airport, known for Asian cuisine, farmland, and rapid growth along the Canada Line.',
    highlights: [
      'Canada Line SkyTrain to downtown in 25 minutes',
      'Richmond Centre, Steveston Village',
      'Agricultural land reserve with unique character',
      'Strong investor and first-time buyer market',
    ],
  },
  'Surrey': {
    i18nKey: 'surrey',
    name: 'Surrey',
    description: 'Metro Vancouver\'s fastest-growing city with affordable entry points, major SkyTrain expansion, and diverse communities.',
    highlights: [
      'Fleetwood, South Surrey / White Rock corridor',
      'SkyTrain expansion to Langley underway',
      'Most affordable detached homes in Metro Van',
      'Young, growing population with strong demand',
    ],
  },
  'Coquitlam': {
    i18nKey: 'coquitlam',
    name: 'Coquitlam',
    description: 'Family-oriented suburb with Evergreen Line SkyTrain access. Strong schools, parks, and a mix of housing types.',
    highlights: [
      'Coquitlam Centre, Burke Mountain, Westwood Plateau',
      'Evergreen SkyTrain extension to Waterfront',
      'Top-ranked school districts',
      'Growing townhouse and detached market',
    ],
  },
  'Port Coquitlam': {
    i18nKey: 'portCoquitlam',
    name: 'Port Coquitlam',
    description: 'Tight-knit community with strong character, river trails, and improving transit connections.',
    highlights: [
      'Traboulay PoCo Trail loop',
      'Downtown PoCo revitalisation',
      'More affordable than Coquitlam neighbours',
      'West Coast Express commuter rail',
    ],
  },
  'Port Moody': {
    i18nKey: 'portMoody',
    name: 'Port Moody',
    description: 'Compact "City of the Arts" at the head of Burrard Inlet, featuring brewery row, waterfront trails, and Evergreen Line access.',
    highlights: [
      'Brewery Row destination dining',
      'Rocky Point Park waterfront',
      'Evergreen SkyTrain connected',
      'Strong condo and townhouse market',
    ],
  },
  'New Westminster': {
    i18nKey: 'newWestminster',
    name: 'New Westminster',
    description: 'Historic city with riverfront character and two SkyTrain lines. Surprisingly affordable condos near transit.',
    highlights: [
      'Quay, Sapperton, Uptown: distinct villages',
      'Two SkyTrain lines: Expo and Millennium',
      'Walkable downtown with heritage buildings',
      'Strong entry-level condo market',
    ],
  },
  'Delta': {
    i18nKey: 'delta',
    name: 'Delta',
    description: 'Three distinct communities (Ladner, Tsawwassen, and North Delta) offering suburban living with ferry access to Vancouver Island.',
    highlights: [
      'Tsawwassen Mills and BC Ferries terminal',
      'Agricultural character in Ladner',
      'North Delta: more urban, family-oriented',
      'Burns Bog ecological reserve',
    ],
  },
  'Pitt Meadows': {
    i18nKey: 'pittMeadows',
    name: 'Pitt Meadows',
    description: 'Rural-suburban character with dyke trail networks, farming heritage, and West Coast Express commuter rail.',
    highlights: [
      'Pitt River dyke trails for cycling',
      'West Coast Express to downtown',
      'Small-town feel with growing amenities',
      'Most affordable in the Tri-Cities corridor',
    ],
  },
  'Maple Ridge': {
    i18nKey: 'mapleRidge',
    name: 'Maple Ridge',
    description: 'Mountain-backed community east of Pitt Meadows with strong family appeal, outdoor recreation, and increasingly connected transit options.',
    highlights: [
      'Gateway to Golden Ears Provincial Park',
      'Maple Ridge town centre revitalisation',
      'West Coast Express commuter rail',
      'Affordable detached homes with acreage options',
      'Growing arts and equestrian community',
    ],
  },
  'Langley city': {
    i18nKey: 'langleyCity',
    name: 'Langley City',
    description: 'Compact urban core within the Township, with a walkable downtown, affordable housing, and SkyTrain extension coming.',
    highlights: [
      'Future SkyTrain terminus',
      'Walkable downtown core',
      'Affordable entry-level homes',
      'Growing arts and culture scene',
    ],
  },
  'Langley district': {
    i18nKey: 'langleyTownship',
    name: 'Langley Township',
    description: 'Expansive suburban-rural community with horse country, wineries, and fast-growing master-planned neighbourhoods.',
    highlights: [
      'Willoughby, Brookswood, Walnut Grove',
      'Fort Langley heritage village',
      'Acreage properties available',
      'Strong new-construction market',
    ],
  },
  'Bowen Island': {
    i18nKey: 'bowenIsland',
    name: 'Bowen Island',
    description: 'Island community 20 minutes by ferry from Horseshoe Bay, with artistic character, nature, and quiet coastal living.',
    highlights: [
      'BC Ferries from Horseshoe Bay',
      'Artistic and creative community',
      'Hiking, kayaking, nature immersion',
      'Limited inventory, unique market',
    ],
  },
  'Lions Bay': {
    i18nKey: 'lionsBay',
    name: 'Lions Bay',
    description: 'Tiny mountain village between Vancouver and Whistler on the Sea-to-Sky Highway.',
    highlights: [
      'Population ~1,300',
      'Sea-to-Sky corridor gateway',
      'Oceanfront mountain setting',
      'Very limited housing stock',
    ],
  },
  'UBC': {
    i18nKey: 'ubc',
    name: 'UBC',
    description: 'University endowment lands with Wesbrook Village, a unique leasehold market surrounded by Pacific Spirit Park.',
    highlights: [
      'Leasehold properties (99-year prepaid leases)',
      'World-class university campus',
      'Pacific Spirit Regional Park',
      'Strong rental demand from students and faculty',
    ],
  },
  'Anmore': {
    i18nKey: 'anmore',
    name: 'Anmore',
    description: 'Rural village adjacent to Buntzen Lake with large lots, mountain views, and a quiet retreat from urban density.',
    highlights: [
      'Buntzen Lake recreation',
      'Large estate-sized lots',
      'Very low density',
      'Popular with outdoor enthusiasts',
    ],
  },
  'Belcarra': {
    i18nKey: 'belcarra',
    name: 'Belcarra',
    description: 'One of Metro Vancouver\'s smallest municipalities, with waterfront properties on Indian Arm and a rural, private character.',
    highlights: [
      'Indian Arm waterfront access',
      'Belcarra Regional Park',
      'Population under 700',
      'Exclusive, private setting',
    ],
  },
  'Barnston island': {
    i18nKey: 'barnstonIsland',
    name: 'Barnston Island',
    description: 'Agricultural island in the Fraser River accessible only by free ferry, a rare and secluded property market.',
    highlights: [
      'Free 5-minute car ferry',
      'Agricultural land reserve',
      'No bridge connection, true island living',
      'Extremely limited market',
    ],
  },
  'White Rock': {
    i18nKey: 'whiteRock',
    name: 'White Rock',
    description: 'Charming seaside city on Semiahmoo Bay with a historic pier, beachfront promenade, and a distinct small-town feel separate from Surrey.',
    highlights: [
      'White Rock Pier and beachfront promenade',
      'Walkable town centre with independent shops',
      'Strong downsizer and retiree market',
      'Mild microclimate, warmest in Metro Vancouver',
    ],
  },
  'Electoral district A': {
    i18nKey: 'electoralDistrictA',
    name: 'Electoral Area A',
    description: 'Unincorporated areas of Metro Vancouver including the University Endowment Lands periphery, Barnston Island surroundings, and remote parcels not within any municipality.',
    highlights: [
      'Governed directly by Metro Vancouver Regional District',
      'Includes rural and semi-rural pockets',
      'Very limited housing market',
      'Unique governance and zoning rules',
    ],
  },
};
